import { useState, useEffect, useCallback } from 'react';

export interface CreditBalance {
  credits: number;
  totalCredits: number;
  // Charter member since (ISO date). Permanent: set once at the charter sale
  // and never cleared, whatever the subscription does later.
  charterSince?: string | null;
  // Hey Joe credit in cents (the dollar wallet, not video tokens).
  walletCents?: number;
}

export interface CreditPackage {
  packageId: string;
  name: string;
  credits: number;
  priceInCents: number;
  popular?: boolean;
}

export interface CreditCost {
  operationType: string;
  cost: number;
  description: string;
}

export interface CreditCheckResult {
  hasCredits: boolean;
  cost: number;
  totalCredits: number;
  credits: number;
  isFree: boolean;
}

export interface CreditTransaction {
  type: 'usage' | 'purchase' | 'subscription_grant' | 'admin_grant';
  amount: number;
  creditType?: string;
  operationType?: string;
  timestamp: string;
  balanceBefore: number;
  balanceAfter: number;
}

export function useCredits() {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = useCallback(() => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  }, []);

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  }, [getAuthToken]);

  // Fetch user's credit balance
  const fetchBalance = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setBalance(null);
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/credits', {
        headers: getHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setBalance(null);
          return null;
        }
        throw new Error('Failed to fetch video token balance');
      }

      const data = await response.json();
      setBalance(data);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getHeaders]);

  // Fetch available credit packages
  const fetchPackages = useCallback(async () => {
    try {
      const response = await fetch('/api/credits/packages');
      if (!response.ok) {
        throw new Error('Failed to fetch video token packs');
      }
      const data = await response.json();
      setPackages(data.packages || []);
      return data.packages || [];
    } catch (err) {
      console.error('Error fetching packages:', err);
      return [];
    }
  }, []);

  // Check if user has enough credits for an operation
  const checkCredits = useCallback(async (operationType: string): Promise<CreditCheckResult | null> => {
    const token = getAuthToken();
    if (!token) {
      return null;
    }

    try {
      const response = await fetch('/api/credits/check', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ operationType }),
      });

      if (!response.ok) {
        if (response.status === 402) {
          const data = await response.json();
          return {
            hasCredits: false,
            cost: data.required,
            totalCredits: data.available,
            credits: data.credits ?? data.available,
            isFree: false,
          };
        }
        throw new Error('Failed to check video tokens');
      }

      const data = await response.json();
      return {
        hasCredits: data.hasCredits,
        cost: data.cost,
        totalCredits: data.totalCredits,
        credits: data.credits ?? data.totalCredits,
        isFree: data.isFree || false,
      };
    } catch (err) {
      console.error('Error checking credits:', err);
      return null;
    }
  }, [getAuthToken, getHeaders]);

  // Get credit cost for an operation type
  const getCreditCost = useCallback(async (operationType: string): Promise<number | null> => {
    try {
      const response = await fetch('/api/credits/packages');
      if (!response.ok) {
        throw new Error('Failed to fetch costs');
      }
      const data = await response.json();
      const costInfo = data.costs?.find((c: CreditCost) => c.operationType === operationType);
      return costInfo?.cost ?? null;
    } catch (err) {
      console.error('Error getting credit cost:', err);
      return null;
    }
  }, []);

  // Initiate purchase flow (returns Stripe checkout URL)
  // useWallet (default on): Hey Joe credit pays first; the card pays the rest.
  const purchaseCredits = useCallback(async (packageId: string, useWallet: boolean = true): Promise<string | null> => {
    const token = getAuthToken();
    if (!token) {
      setError('Authentication required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/credits/purchase', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ packageId, useWallet }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to initiate purchase');
      }

      const data = await response.json();
      return data.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getHeaders]);

  // Fetch transaction history
  const fetchHistory = useCallback(async (limit: number = 50): Promise<CreditTransaction[]> => {
    const token = getAuthToken();
    if (!token) {
      return [];
    }

    try {
      const response = await fetch(`/api/credits/history?limit=${limit}`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }

      const data = await response.json();
      return data.transactions || [];
    } catch (err) {
      console.error('Error fetching history:', err);
      return [];
    }
  }, [getAuthToken, getHeaders]);

  // Get formatted credit display (e.g., "5 tokens")
  const getCreditDisplay = useCallback(() => {
    if (!balance) return null;
    const total = balance.totalCredits;
    return `${total} token${total !== 1 ? 's' : ''}`;
  }, [balance]);

  // Get credit status color based on balance
  const getCreditStatusColor = useCallback(() => {
    if (!balance) return 'text-muted-foreground';
    if (balance.totalCredits === 0) return 'text-destructive';
    if (balance.totalCredits <= 3) return 'text-yellow-500';
    return 'text-primary';
  }, [balance]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!getAuthToken();
  }, [getAuthToken]);

  // Refresh balance on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated()) {
      fetchBalance();
    }
  }, [fetchBalance, isAuthenticated]);

  return {
    // State
    balance,
    packages,
    loading,
    error,

    // Actions
    fetchBalance,
    fetchPackages,
    checkCredits,
    getCreditCost,
    purchaseCredits,
    fetchHistory,

    // Helpers
    getCreditDisplay,
    getCreditStatusColor,
    isAuthenticated,

    // Computed values
    totalCredits: balance?.totalCredits ?? 0,
    credits: balance?.credits ?? 0,
    hasCredits: (balance?.totalCredits ?? 0) > 0,
  };
}

// Operation type constants for type safety
export const CREDIT_OPERATIONS = {
  SHOTSTACK_RENDER: 'shotstack_render',
  SHOTSTACK_PREVIEW: 'shotstack_preview',
  IMAGE_GENERATION: 'image_generation',
  VIDEO_GENERATION: 'video_generation',
  MUSIC_GENERATION: 'music_generation',
  BACKGROUND_REMOVAL: 'background_removal',
  COMPOSITE_BACKGROUND: 'composite_background',
  SCRIPT_ANALYSIS: 'script_analysis',
  AUDIO_ENHANCEMENT: 'audio_enhancement',
  SCENE_EDIT_3: 'scene_edit_3',
  SCENE_EDIT_5: 'scene_edit_5',
  SCENE_GUIDE_FRAME: 'scene_guide_frame',
  SCENE_EDIT_10: 'scene_edit_10',
  SCENE_EDIT_20: 'scene_edit_20',
  SCENE_EDIT_30: 'scene_edit_30',
} as const;

export type CreditOperation = typeof CREDIT_OPERATIONS[keyof typeof CREDIT_OPERATIONS];
